import React, {useState, useEffect} from 'react';
import {Button, Input, Select, Switch, Form, message, Card, Typography, Upload, Radio} from 'antd';
import type {UploadFile} from 'antd/es/upload/interface';
import {UploadOutlined} from '@ant-design/icons';
import campaignService from '../../../services/campaignService';
import countryService from '../../../services/countryService';
import cityService from '../../../services/cityService';
import stateService, {StateOption} from '../../../services/stateService';
import {useNavigate, useParams} from 'react-router-dom';
import {useIsMobile} from "../../../utils/helpers";
import RichText from '../../../components/molecules/Forms/RichText/component';
import DOMPurify from "dompurify";
import GeoRadiusPicker from '../../../components/organisms/Forms/GeoRadiusPicker/component';
import {
    buildGeographicLocation,
    resolveCampaignGeoFormValues,
    TargetingMode,
    validateClickRate
} from '../../../utils/campaignGeo';

const {Title} = Typography;
const {Option} = Select;
const US_COUNTRY_ISO2 = 'US';

const getFilenameFromSignedUrl = (signedUrl?: string) => {
    if (!signedUrl) return '';
    try {
        const url = new URL(signedUrl);

        const pathName = url.pathname.split('/').pop() || '';
        const pathFile = decodeURIComponent(pathName);
        if (pathFile) return pathFile;

        const cd = url.searchParams.get('response-content-disposition') || '';
        const match = cd.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
        if (match?.[1]) return decodeURIComponent(match[1]);

        return 'file';
    } catch {
        return 'file';
    }
};

const toExistingUploadFile = (signedUrl: string, uid: string): UploadFile => ({
    uid,
    name: getFilenameFromSignedUrl(signedUrl),
    status: 'done',
    url: signedUrl,
});

const UpdateActiveCampaign: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const [form] = Form.useForm();
    const isMobile = useIsMobile();
    const [showAssets, setShowAssets] = useState(false);
    const [isGlobal, setIsGlobal] = useState(true);
    const [targetingMode, setTargetingMode] = useState<TargetingMode>('location');
    const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
    const [selectedState, setSelectedState] = useState<string | null>(null);
    const [selectedCity, setSelectedCity] = useState<string | null>(null);
    const [countries, setCountries] = useState<{ iso2: string; name: string }[]>([]);
    const [states, setStates] = useState<StateOption[]>([]);
    const [cities, setCities] = useState<string[]>([]);
    const [updatedCampaignData, setUpdatedCampaignData] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
    const [mediaFile, setMediaFile] = useState<File | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const [logoList, setLogoList] = useState<UploadFile[]>([]);
    const [thumbnailList, setThumbnailList] = useState<UploadFile[]>([]);
    const [mediaList, setMediaList] = useState<UploadFile[]>([]);
    const [videoFile, setVideoFile] = useState<File | null>(null);
    const [videoList, setVideoList] = useState<UploadFile[]>([]);
    const isUnitedStatesSelected = selectedCountry === US_COUNTRY_ISO2;

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const data = await countryService.getCountries();
                setCountries(data);
            } catch (error) {
                console.error('Error fetching countries:', error);
                message.error('Failed to load countries.');
            }
        };
        fetchCountries();
    }, []);

    useEffect(() => {
        const fetchCampaignData = async () => {
            try {
                const data = await campaignService.getCampaignById(id as string);
                const geoFormValues = resolveCampaignGeoFormValues(data.geographicLocation, countries);

                setUpdatedCampaignData(data);

                form.setFieldsValue({
                    description: data.description,
                    clickRate: data.clickRate,
                    isGlobal: geoFormValues.isGlobal,
                    country: geoFormValues.countryValue,
                    state: geoFormValues.stateValue,
                    city: geoFormValues.cityValue,
                    geoRadius: geoFormValues.geoRadius,
                    reviewNeeded: data.reviewNeeded || false
                });

                setIsGlobal(geoFormValues.isGlobal);
                setTargetingMode(geoFormValues.targetingMode);
                setSelectedCountry(geoFormValues.countryValue);
                setSelectedState(geoFormValues.stateValue);
                setSelectedCity(geoFormValues.cityValue);

                if (geoFormValues.countryObj?.iso2 === US_COUNTRY_ISO2) {
                    const stateData = await stateService.getStatesByCountry(geoFormValues.countryObj.iso2);
                    setStates(stateData);
                }

                if (geoFormValues.countryObj && (geoFormValues.countryObj.iso2 !== US_COUNTRY_ISO2 || geoFormValues.stateValue)) {
                    const cityData = await cityService.getCitiesByCountry(
                        geoFormValues.countryObj.iso2,
                        geoFormValues.countryObj.iso2 === US_COUNTRY_ISO2 ? geoFormValues.stateValue : null
                    );
                    setCities(cityData);
                }

                const logoUrl = data.assets?.logo?.signedUrl;
                const thumbUrl = data.assets?.thumbnail?.signedUrl;
                const mediaUrl = data.assets?.media?.signedUrl;
                const videoUrl = data.assets?.video?.compressed?.signedUrl;

                setLogoList(logoUrl ? [toExistingUploadFile(logoUrl, 'existing-logo')] : []);
                setThumbnailList(thumbUrl ? [toExistingUploadFile(thumbUrl, 'existing-thumb')] : []);
                setMediaList(mediaUrl ? [toExistingUploadFile(mediaUrl, 'existing-media')] : []);
                setVideoList(videoUrl ? [toExistingUploadFile(videoUrl, 'existing-video')] : []);
            } catch (error) {
                console.error('Error fetching campaign:', error);
                message.error('Failed to load campaign details.');
            } finally {
                setLoading(false);
            }
        };

        fetchCampaignData();
    }, [form, id, countries]);

    const handleSubmit = async (values: any) => {
        if (submitting) return;

        const geographicLocation = buildGeographicLocation(values, isGlobal, targetingMode);

        const formData = new FormData();

        const safeToStoreDescription = DOMPurify.sanitize(values.description || '', {
            ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'ul', 'ol', 'li'],
            ALLOWED_ATTR: [],
        });

        formData.append('description', safeToStoreDescription);
        formData.append('clickRate', String(values.clickRate));
        formData.append('geographicLocation', JSON.stringify(geographicLocation));
        formData.append('reviewNeeded', String(values.reviewNeeded));

        // Append files if they exist
        if (logoFile) {
            formData.append('logo', logoFile);
        }
        if (thumbnailFile) {
            formData.append('thumbnail', thumbnailFile);
        }
        if (mediaFile) {
            formData.append('media', mediaFile);
        }
        if (videoFile) {
            formData.append('video', videoFile);
        }

        setSubmitting(true);
        try {
            const result = await campaignService.updateActiveCampaign(id as string, formData);
            if (result._id) {
                message.success('Campaign updated successfully!');
                setUpdatedCampaignData(result);
                // Reset file states
                setLogoFile(null);
                setThumbnailFile(null);
                setMediaFile(null);
                setVideoFile(null);
                navigate(`/campaigns/${result._id}`);
            } else {
                message.error('Failed to update campaign. Please try again.');
            }
        } catch (error: any) {
            console.error('Error updating campaign:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update campaign. Please try again.';
            message.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCountryChange = async (iso2: string) => {
        const countryObj = countries.find(country => country.iso2 === iso2);
        if (!countryObj) return;
        setSelectedCountry(countryObj.iso2);
        setCities([]);
        setStates([]);

        if (countryObj.iso2 === US_COUNTRY_ISO2) {
            const stateData = await stateService.getStatesByCountry(countryObj.iso2);
            setStates(stateData);
        }

        if (countryObj.iso2 !== US_COUNTRY_ISO2) {
            try {
                const cityData = await cityService.getCitiesByCountry(countryObj.iso2);
                setCities(cityData);
            } catch (error) {
                console.error('Error fetching cities:', error);
                message.error('Failed to load cities.');
            }
        }

        setSelectedCity(null);
        setSelectedState(null);
        form.setFieldsValue({state: null, city: null});
    };

    const handleStateChange = async (value?: string) => {
        const state = value || null;
        setSelectedState(state);
        setSelectedCity(null);
        setCities([]);
        form.setFieldsValue({state, city: null});

        if (!state || selectedCountry !== US_COUNTRY_ISO2) return;

        try {
            const cityData = await cityService.getCitiesByCountry(US_COUNTRY_ISO2, state);
            setCities(cityData);
        } catch (error) {
            console.error('Error fetching cities:', error);
            message.error('Failed to load cities.');
        }
    };

    const keepOnlyLast = (list: UploadFile[]) => (list?.length ? [list[list.length - 1]] : []);

    const beforeUpload = (file: File) => {
        const fileName = file.name.toLowerCase();

        const isVideo =
            file.type.startsWith('video/') ||
            fileName.endsWith('.mp4') ||
            fileName.endsWith('.mov') ||
            fileName.endsWith('.webm');

        const limit = isVideo
            ? 100 * 1024 * 1024 // 100MB
            : 5 * 1024 * 1024;  // 5MB images

        if (file.size > limit) {
            message.error(
                isVideo
                    ? 'Video must be smaller than 100 MB'
                    : 'Image must be smaller than 5 MB'
            );
            return Upload.LIST_IGNORE;
        }

        return false;
    };

    if (loading) return <p>Loading...</p>;
    if (!updatedCampaignData) return <p>Campaign not found.</p>;

    return (
        <div style={{width: '100%', maxWidth: 1100, margin: '0 auto', padding: isMobile ? 12 : 20}}>
            <Card
                style={{
                    width: '100%',
                    maxWidth: 650,
                    margin: '0 auto',
                    padding: isMobile ? 12 : 24,
                    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                    borderRadius: 10,
                }}
                bodyStyle={{padding: isMobile ? 12 : 24}}
            >
                <Title level={4} className={"title"}>Update active campaign</Title>
                <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{isGlobal: true}}>
                    <Form.Item label="Description" name="description">
                        <RichText placeholder="Write a description about your campaign..."/>
                    </Form.Item>

                    <Form.Item
                        label="Click rate ($ per click)"
                        name="clickRate"
                        dependencies={["country", "state", "city", "geoRadius"]}
                        rules={[
                            {required: true, message: "Click rate is required"},
                            ({getFieldValue}) => ({
                                validator(_, value) {
                                    const country = getFieldValue("country");
                                    const state = getFieldValue("state");
                                    const city = getFieldValue("city");
                                    const geoRadius = getFieldValue("geoRadius");

                                    return validateClickRate(value, country, state, city, geoRadius);
                                },
                            }),
                        ]}
                    >
                        <Input type="number" step="0.01"/>
                    </Form.Item>

                    <Form.Item
                        label="Review needed"
                        name="reviewNeeded"
                        valuePropName="checked"
                    >
                        <Switch
                            checkedChildren="Yes"
                            unCheckedChildren="No"
                        />
                    </Form.Item>

                    <Form.Item label="Geographic targeting">
                        <Switch checked={isGlobal} onChange={(checked) => {
                            setIsGlobal(checked);
                            if (checked) {
                                setSelectedCountry(null);
                                setSelectedState(null);
                                setSelectedCity(null);
                                setTargetingMode('location');
                                form.setFieldsValue({country: null, state: null, city: null, geoRadius: null});
                            }
                        }} checkedChildren="Global" unCheckedChildren="Custom"/>
                    </Form.Item>

                    {!isGlobal && (
                        <>
                            <Form.Item label="Targeting method">
                                <Radio.Group
                                    value={targetingMode}
                                    onChange={(event) => {
                                        const nextMode = event.target.value;
                                        setTargetingMode(nextMode);
                                        if (nextMode === 'map') {
                                            setSelectedCountry(null);
                                            setSelectedState(null);
                                            setSelectedCity(null);
                                            setStates([]);
                                            setCities([]);
                                            form.setFieldsValue({country: null, state: null, city: null, geoRadius: null});
                                        } else {
                                            form.setFieldsValue({geoRadius: null});
                                        }
                                    }}
                                >
                                    <Radio.Button value="location">Country / state / city</Radio.Button>
                                    <Radio.Button value="map">Pick on map</Radio.Button>
                                </Radio.Group>
                            </Form.Item>

                            {targetingMode === 'location' ? (
                                <>
                                    <Form.Item label="Country" name="country"
                                               rules={[{required: true, message: "Select a country"}]}>
                                        <Select showSearch value={selectedCountry} onChange={handleCountryChange}
                                                placeholder="Country">
                                            {countries.map((country) => (
                                                <Option key={country.iso2} value={country.iso2}>{country.name}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                    {isUnitedStatesSelected && (
                                        <Form.Item label="State" name="state"
                                                   rules={[{required: true, message: "Select a state"}]}>
                                            <Select showSearch value={selectedState} onChange={handleStateChange} placeholder="State">
                                                {states.map((state) => (
                                                    <Option key={state.code} value={state.name}>{state.name}</Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    )}
                                    <Form.Item label="City" name="city">
                                        <Select showSearch allowClear value={selectedCity} onChange={(value) => {
                                            setSelectedCity(value || null);
                                            form.setFieldsValue({city: value || null});
                                        }} placeholder="City (Optional)" disabled={!selectedCountry || (isUnitedStatesSelected && !selectedState)}>
                                            {cities.map((city) => (
                                                <Option key={city} value={city}>{city}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </>
                            ) : (
                                <Form.Item
                                    label="Target area"
                                    name="geoRadius"
                                    rules={[{required: true, message: "Choose a location and radius"}]}
                                >
                                    <GeoRadiusPicker/>
                                </Form.Item>
                            )}
                        </>
                    )}

                    <Form.Item label="Campaign assets">
                        <Switch
                            checked={showAssets}
                            onChange={(checked) => {
                                setShowAssets(checked);
                                if (!checked) {
                                    setLogoFile(null);
                                    setThumbnailFile(null);
                                    setMediaFile(null);
                                    setVideoFile(null);
                                }
                            }}
                            checkedChildren="Show"
                            unCheckedChildren="Hide"
                        />
                    </Form.Item>

                    {showAssets && (
                        <div style={{
                            padding: isMobile ? 10 : 12,
                            border: '1px solid #f0f0f0',
                            borderRadius: 8,
                            marginBottom: 12,
                            background: '#fafafa',
                        }}>
                            <Form.Item label="Logo" extra="Image only • Max 5 MB">
                                <Upload
                                    beforeUpload={beforeUpload}
                                    maxCount={1}
                                    accept="image/*"
                                    fileList={logoList}
                                    listType="text"
                                    showUploadList={{showPreviewIcon: false}}
                                    onRemove={() => {
                                        setLogoFile(null);
                                        setLogoList([]);
                                    }}
                                    onChange={(info) => {
                                        const next = keepOnlyLast(info.fileList);
                                        setLogoList(next);

                                        const f = next?.[0]?.originFileObj;
                                        setLogoFile(f instanceof File ? f : null);
                                    }}

                                >
                                    <Button block={isMobile} icon={<UploadOutlined/>}>Upload logo</Button>
                                </Upload>
                            </Form.Item>

                            <Form.Item label="Thumbnail" extra="Image only • Max 5 MB">
                                <Upload
                                    beforeUpload={beforeUpload}
                                    maxCount={1}
                                    accept="image/*"
                                    fileList={thumbnailList}
                                    listType="text"
                                    showUploadList={{showPreviewIcon: false}}
                                    onRemove={() => {
                                        setThumbnailFile(null);
                                        setThumbnailList([]);
                                    }}
                                    onChange={(info) => {
                                        const next = keepOnlyLast(info.fileList);
                                        setThumbnailList(next);

                                        const f = next?.[0]?.originFileObj;
                                        setThumbnailFile(f instanceof File ? f : null);
                                    }}

                                >
                                    <Button block={isMobile} icon={<UploadOutlined/>}>Upload thumbnail</Button>
                                </Upload>
                            </Form.Item>

                            <Form.Item label="Media" extra="Image only • Max 5 MB">
                                <Upload
                                    beforeUpload={beforeUpload}
                                    maxCount={1}
                                    accept="image/*"
                                    fileList={mediaList}
                                    listType="text"
                                    showUploadList={{showPreviewIcon: false}}
                                    onRemove={() => {
                                        setMediaFile(null);
                                        setMediaList([]);
                                    }}
                                    onChange={(info) => {
                                        const next = keepOnlyLast(info.fileList);
                                        setMediaList(next);

                                        const f = next?.[0]?.originFileObj;
                                        setMediaFile(f instanceof File ? f : null);
                                    }}

                                >
                                    <Button block={isMobile} icon={<UploadOutlined/>}>Upload media</Button>
                                </Upload>
                            </Form.Item>

                            <Form.Item label="Video" extra="MP4 • Max 100 MB">
                                <Upload
                                    beforeUpload={beforeUpload}
                                    maxCount={1}
                                    accept="video/mp4"
                                    fileList={videoList}
                                    listType="text"
                                    showUploadList={{ showPreviewIcon: false }}
                                    onRemove={() => {
                                        setVideoFile(null);
                                        setVideoList([]);
                                    }}
                                    onChange={(info) => {
                                        const next = keepOnlyLast(info.fileList);
                                        setVideoList(next);

                                        const f = next?.[0]?.originFileObj;
                                        setVideoFile(f instanceof File ? f : null);
                                    }}
                                >
                                    <Button block={isMobile} icon={<UploadOutlined />}>
                                        Upload video
                                    </Button>
                                </Upload>
                            </Form.Item>
                        </div>
                    )}

                    <Form.Item style={{marginBottom: 0}} shouldUpdate>
                        {() => {
                            const hasErrors = form.getFieldsError().some(({errors}) => errors.length);

                            return (
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    block={isMobile}
                                    disabled={hasErrors || submitting}
                                    loading={submitting}
                                    style={{width: isMobile ? '100%' : 'auto'}}
                                >
                                    {submitting ? 'Updating...' : 'Update campaign'}
                                </Button>
                            );
                        }}
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default UpdateActiveCampaign;